"use client"

import React, { useMemo, useState } from "react"
import { addArticleToCurrentBasketAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { CheckCircleIcon, ShoppingCart, XCircleIcon } from "lucide-react"
import { toast } from "sonner"

import { trackAddToBasket, trackFFProductClick } from "@/lib/tracking/tracking-helper"
import { Button, Input } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import type { UbBasketQuickOrderStoryblok } from "@/components/storyblok/component-types"

interface BasketQuickOrderProps {
  blok: UbBasketQuickOrderStoryblok
}

export default function BasketQuickOrderClient(props: BasketQuickOrderProps) {
  const [productId, setProductId] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const { basketData, basketLineItems, setBasketData, setIsLoading } = useUbStorefrontState()

  const hasLineItems = useMemo(() => {
    return (basketData?.data?.totalProductQuantity ?? 0) > 0 && basketLineItems && basketLineItems.length > 0
  }, [basketData?.data?.totalProductQuantity, basketLineItems])

  function updateBasketData(productId: string) {
    console.log("adding product: " + productId)
    setIsLoading(true)
    if (productId) {
      addArticleToCurrentBasketAction(productId, 1)
        .then((res) => {
          setBasketData(res.basketData)
          if (res.result === "NOK") {
            setErrorMessage(res.message)
            toast.error(props.blok.error_notification ?? "Artikel konnte nicht hinzugefügt werden.", {
              icon: <XCircleIcon className={"size-4 text-red-500"} />,
              description: "",
            })
          } else {
            toast.success(props.blok.success_notification ?? "Artikel erfolgreich hinzugefügt", {
              icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
              description: "",
            })
          }
          trackAddToBasket(res.isoLocale, res.isB2B, res.product, res.prices, res.quantity)
          trackFFProductClick(res.product, "direct", 1, res.product?.title)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error("QuickOrder add error", err)
          setErrorMessage("Artikel konnte nicht hinzugefügt werden.")
          toast.error("Artikel konnte nicht hinzugefügt werden.")
          setIsLoading(false)
        })
    }
  }

  const handleChange = (event) => {
    const { value } = event.target
    setProductId(value ? value.trim() : value) //https://udobaer.atlassian.net/browse/UBW-761 Always trim
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setErrorMessage(undefined)
    if (productId && productId !== "") {
      let effectiveProductId = productId
      if (productId.charAt(0) === "A" || productId.charAt(0) === "a") {
        effectiveProductId = productId.substring(1)
      }
      updateBasketData(effectiveProductId)
    }
  }

  return (
    <div>
      {(hasLineItems || props.blok.showWhenBasketIsEmpty) && (
        <div
          className={cn("min-h-20", {
            "min-h-8": props.blok.hideLabel,
          })}
        >
          <div className={"flex flex-col gap-2"}>
            {!props.blok.hideLabel && (
              <h2
                className={cn("text-sm font-bold sm:text-sm", {
                  "text-sm": props.blok.compact,
                })}
              >
                {props.blok["label_quick_order"]}
              </h2>
            )}
            <form className={"grid grid-cols-12 items-center gap-2"} onSubmit={handleSubmit}>
              <Input
                className={cn("col-span-12 text-sm sm:col-span-6 sm:text-sm md:col-span-8", {
                  "text-sm sm:text-sm": props.blok.compact,
                })}
                placeholder={props.blok?.text_input_placeholder}
                type="text"
                name="productId"
                value={productId}
                onChange={handleChange}
              />

              <Button
                className={"col-span-12 w-full sm:col-span-6 md:col-span-4"}
                size={"productCardIcon"}
                variant={props.blok.compact ? "outline_secondary" : "default"}
                onClick={handleSubmit}
              >
                <ShoppingCart className={"size-4"} />
              </Button>
            </form>
            {errorMessage && <p className={"text-xs text-red-500"}>{errorMessage}</p>}
          </div>
        </div>
      )}
    </div>
  )
}
